import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Search, ChevronDown, ChevronUp, TriangleAlert as AlertTriangle, User, RefreshCw, RotateCw, Trash2, Video, FileText, Star, Disc, BarChart2, Loader as Loader2 } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { DateRangePicker } from '../components/DateRangePicker';
import { ReportViewerModal } from '../components/ReportViewerModal';
import { SyncCalculationService } from '../services/SyncCalculationService';
import { DebugService, PerformanceAnalysisRow } from '../services/DebugService';
import { Recording } from '../services/RecordingService';

interface DayGroup {
  date: string;
  rows: PerformanceAnalysisRow[];
}

function groupByDate(rows: PerformanceAnalysisRow[]): DayGroup[] {
  const map = new Map<string, PerformanceAnalysisRow[]>();
  for (const row of rows) {
    const date = row.pa_created_at.split('T')[0];
    const existing = map.get(date);
    if (existing) {
      existing.push(row);
    } else {
      map.set(date, [row]);
    }
  }
  const groups: DayGroup[] = [];
  for (const [date, dateRows] of map.entries()) {
    groups.push({ date, rows: dateRows });
  }
  groups.sort((a, b) => b.date.localeCompare(a.date));
  return groups;
}

function formatDateHeader(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

function getDateNDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}

function getTodayStr(): string {
  return new Date().toISOString().split('T')[0];
}

const DAYS_PER_CHUNK = 30;

export const TechnicalDebug: React.FC = () => {
  const navigate = useNavigate();
  const { user, profile } = useUser();

  const [allData, setAllData] = useState<PerformanceAnalysisRow[]>([]);
  const [dayGroups, setDayGroups] = useState<DayGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [oldestLoadedDate, setOldestLoadedDate] = useState<string>('');
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  const [retryingRecordings, setRetryingRecordings] = useState<Set<string>>(new Set());
  const [retryingVideoProcessing, setRetryingVideoProcessing] = useState<Set<string>>(new Set());
  const [deletingRows, setDeletingRows] = useState<Set<string>>(new Set());
  const [rescoringRows, setRescoringRows] = useState<Set<string>>(new Set());
  const [preprocessingRecordings, setPreprocessingRecordings] = useState<Set<string>>(new Set());
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [selectedReportTitle, setSelectedReportTitle] = useState('');
  const [selectedLyricsUrl, setSelectedLyricsUrl] = useState<string | undefined>();
  const [selectedVocalUrl, setSelectedVocalUrl] = useState<string | undefined>();
  const [selectedBackingUrl, setSelectedBackingUrl] = useState<string | undefined>();
  const [selectedFinalUrl, setSelectedFinalUrl] = useState<string | undefined>();
  const [syncingRecordings, setSyncingRecordings] = useState<Set<string>>(new Set());
  const [bulkCalculatingSync, setBulkCalculatingSync] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0 });

  // Filters
  const [filterUserId, setFilterUserId] = useState<string>('');
  const [filterRecordingId, setFilterRecordingId] = useState<string>('');
  const [filterPerformanceId, setFilterPerformanceId] = useState<string>('');

  const hasActiveFilters = !!(filterUserId || filterRecordingId || filterPerformanceId);

  // Flat pagination mode (when filters active)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [flatData, setFlatData] = useState<PerformanceAnalysisRow[]>([]);

  // DateRangePicker dates (used in flat mode)
  const [startDate, setStartDate] = useState<string>(() => {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return firstDayOfMonth.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(getTodayStr());

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (profile?.role !== 'technical' && profile?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
  }, [user, profile, navigate]);

  // Infinite scroll mode: initial load
  useEffect(() => {
    if (!user || (profile?.role !== 'technical' && profile?.role !== 'admin')) return;
    if (hasActiveFilters) return;
    loadInitialChunk();
  }, [user, profile]);

  // Flat mode: load data when filters change
  useEffect(() => {
    if (!user || (profile?.role !== 'technical' && profile?.role !== 'admin')) return;
    if (!hasActiveFilters) return;
    loadFlatData();
  }, [filterUserId, filterRecordingId, filterPerformanceId, startDate, endDate, currentPage]);

  const loadInitialChunk = async () => {
    setLoading(true);
    setError(null);
    setAllData([]);
    setDayGroups([]);
    setHasMore(true);
    setExpandedDays(new Set());

    const end = getTodayStr();
    const start = getDateNDaysAgo(DAYS_PER_CHUNK - 1);

    try {
      const { data } = await DebugService.fetchDebugData({
        startDate: start,
        endDate: end,
        pageSize: 5000,
        pageNum: 1
      });

      setAllData(data);
      const groups = groupByDate(data);
      setDayGroups(groups);
      setOldestLoadedDate(start);

      if (groups.length > 0) {
        setExpandedDays(new Set([groups[0].date]));
      }

      setHasMore(true);
    } catch (err: any) {
      console.error('Error loading debug data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreDays = useCallback(async () => {
    if (loadingMore || !hasMore || hasActiveFilters) return;

    setLoadingMore(true);
    setError(null);

    const prevOldest = oldestLoadedDate;
    const newEnd = new Date(prevOldest);
    newEnd.setDate(newEnd.getDate() - 1);
    const newEndStr = newEnd.toISOString().split('T')[0];

    const newStart = new Date(newEnd);
    newStart.setDate(newStart.getDate() - (DAYS_PER_CHUNK - 1));
    const newStartStr = newStart.toISOString().split('T')[0];

    try {
      const { data } = await DebugService.fetchDebugData({
        startDate: newStartStr,
        endDate: newEndStr,
        pageSize: 5000,
        pageNum: 1
      });

      if (data.length === 0) {
        setHasMore(false);
      } else {
        const combined = [...allData, ...data];
        setAllData(combined);
        setDayGroups(groupByDate(combined));
        setOldestLoadedDate(newStartStr);
      }
    } catch (err: any) {
      console.error('Error loading more data:', err);
      setError(err.message || 'Failed to load more data');
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, hasActiveFilters, oldestLoadedDate, allData]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (hasActiveFilters) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && !loadingMore && hasMore) {
          loadMoreDays();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasActiveFilters, loading, loadingMore, hasMore, loadMoreDays]);

  const loadFlatData = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, totalCount: count } = await DebugService.fetchDebugData({
        filterUserId: filterUserId || undefined,
        filterRecordingId: filterRecordingId || undefined,
        filterPerformanceId: filterPerformanceId || undefined,
        startDate,
        endDate,
        pageNum: currentPage,
        pageSize
      });

      setFlatData(data);
      setTotalCount(count);
    } catch (err: any) {
      console.error('Error loading debug data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (hasActiveFilters) {
      loadFlatData();
    } else {
      loadInitialChunk();
    }
  };

  const handleClearFilters = () => {
    setFilterUserId('');
    setFilterRecordingId('');
    setFilterPerformanceId('');
    setCurrentPage(1);
    loadInitialChunk();
  };

  const toggleDay = (date: string) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
  };

  const handleRetryVocalProcessing = async (recordingId: string, recordingUrl: string) => {
    if (!recordingId || !recordingUrl) return;
    setRetryingRecordings(prev => new Set(prev).add(recordingId));
    try {
      await DebugService.retryVocalProcessing(recordingId, recordingUrl);
      handleRefresh();
    } catch (err: any) {
      setError(err.message || 'Failed to retry vocal processing');
    } finally {
      setRetryingRecordings(prev => { const s = new Set(prev); s.delete(recordingId); return s; });
    }
  };

  const handleRetryVideoProcessing = async (recordingId: string, recordingUrl: string, vocalsOnlyUrl: string, backingTrackUrl?: string) => {
    if (!recordingId || !recordingUrl || !vocalsOnlyUrl) return;
    setRetryingVideoProcessing(prev => new Set(prev).add(recordingId));
    try {
      await DebugService.retryVideoProcessing(recordingId, recordingUrl, vocalsOnlyUrl, backingTrackUrl);
      handleRefresh();
    } catch (err: any) {
      setError(err.message || 'Failed to retry video processing');
    } finally {
      setRetryingVideoProcessing(prev => { const s = new Set(prev); s.delete(recordingId); return s; });
    }
  };

  const handleDeletePerformance = async (performanceAnalysisId: string, performanceId: string) => {
    if (!performanceAnalysisId || !performanceId) return;
    setDeletingRows(prev => new Set(prev).add(performanceAnalysisId));
    try {
      await DebugService.deletePerformance(performanceAnalysisId, performanceId);
      handleRefresh();
    } catch (err: any) {
      setError(err.message || 'Failed to delete performance records');
    } finally {
      setDeletingRows(prev => { const s = new Set(prev); s.delete(performanceAnalysisId); return s; });
    }
  };

  const handleRescore = async (row: PerformanceAnalysisRow) => {
    if (!row.pa_id || !row.p_id || !row.r_vocals_only_url || !row.s_lead_vocal_url) {
      setError('Cannot rescore: missing vocals URL or lead vocal reference');
      return;
    }
    setRescoringRows(prev => new Set(prev).add(row.pa_id));
    try {
      await DebugService.rescorePerformance(row);
      handleRefresh();
    } catch (err: any) {
      setError(err.message || 'Failed to rescore performance');
    } finally {
      setRescoringRows(prev => { const s = new Set(prev); s.delete(row.pa_id); return s; });
    }
  };

  const handleTriggerPreprocessing = async (recordingId: string) => {
    if (!recordingId) return;
    setPreprocessingRecordings(prev => new Set(prev).add(recordingId));
    try {
      await DebugService.triggerPreprocessing(recordingId);
      handleRefresh();
    } catch (err: any) {
      setError(err.message || 'Failed to trigger audio preprocessing');
    } finally {
      setPreprocessingRecordings(prev => { const s = new Set(prev); s.delete(recordingId); return s; });
    }
  };

  const handleSingleRecalculateSync = async (row: PerformanceAnalysisRow) => {
    if (!row.r_id || !row.r_recording_url || !row.s_audio_url) return;
    setSyncingRecordings(prev => new Set(prev).add(row.r_id));
    try {
      const recording = {
        id: row.r_id,
        recording_url: row.r_recording_url,
        audio_sync_offset: row.r_audio_sync_offset,
        song: { audio_url: row.s_audio_url },
      } as any;
      await SyncCalculationService.calculateAndSaveSyncOffset(recording);
      handleRefresh();
    } catch (err: any) {
      setError(err.message || 'Failed to recalculate audio sync offset');
    } finally {
      setSyncingRecordings(prev => { const s = new Set(prev); s.delete(row.r_id); return s; });
    }
  };

  const handleBulkRecalculateSync = async () => {
    setBulkCalculatingSync(true);
    setError(null);
    setSyncProgress({ current: 0, total: 0 });
    try {
      const recordings = await DebugService.fetchRecordingsForBulkSync();
      if (!recordings || recordings.length === 0) {
        setError('No recordings found that need sync calculation');
        return;
      }
      const recordingsWithBackingTrack = recordings.filter(
        (r: any) => r.song?.audio_url
      ) as unknown as Recording[];
      if (recordingsWithBackingTrack.length === 0) {
        setError('No recordings with backing tracks found that need sync calculation');
        return;
      }
      setSyncProgress({ current: 0, total: recordingsWithBackingTrack.length });
      for (let i = 0; i < recordingsWithBackingTrack.length; i++) {
        try {
          await SyncCalculationService.calculateAndSaveSyncOffset(recordingsWithBackingTrack[i]);
          setSyncProgress({ current: i + 1, total: recordingsWithBackingTrack.length });
        } catch (err) {
          console.error(`Failed to process recording ${recordingsWithBackingTrack[i].id}:`, err);
        }
      }
      handleRefresh();
    } catch (err: any) {
      setError(err.message || 'Failed to bulk recalculate audio sync');
    } finally {
      setBulkCalculatingSync(false);
      setSyncProgress({ current: 0, total: 0 });
    }
  };

  const handleExportCSV = () => {
    const exportData = hasActiveFilters ? flatData : allData;
    if (exportData.length === 0) {
      setError('No data to export');
      return;
    }

    const headers = [
      'PA_ID', 'PA_Performance_ID', 'PA_Pitch_Accuracy', 'PA_Timing_Accuracy',
      'PA_Note_Transition_Score', 'PA_Overall_Score', 'PA_Detailed_Feedback',
      'PA_Created_At', 'PA_Status_Code', 'PA_Error_Message', 'PA_Coaching_Response',
      'P_ID', 'P_User_ID', 'P_Song_ID', 'P_Vocal_Recording_URL', 'P_Duration_Seconds',
      'P_Score', 'P_Vocal_Score_Data', 'P_Created_At', 'P_Updated_At',
      'P_Video_Recording_URL', 'P_Video_Thumbnail_URL', 'P_Video_Duration_Seconds', 'P_Recording_ID',
      'R_ID', 'R_User_ID', 'R_Song_ID', 'R_Recording_URL', 'R_Recording_Type',
      'R_Duration', 'R_Created_At', 'R_Updated_At',
      'R_Title', 'R_Shared_URL', 'R_Shared', 'R_Shared_At', 'R_Vocals_Only_URL',
      'R_Upload_Status', 'R_Local_URI', 'R_Processing_Command_ID', 'R_Processing_Status', 'R_Processing_Error',
      'R_Vocals_Preprocessed_URL', 'R_Preprocessing_Status', 'R_Preprocessing_Error', 'R_Audio_Sync_Offset',
      'S_ID', 'S_Title', 'S_Artist', 'S_Genre', 'S_Release_Year', 'S_Difficulty',
      'S_Duration', 'S_Audio_URL', 'S_Cover_URL', 'S_Lead_Vocal_URL',
      'S_Distribution_Eligible', 'S_Created_At', 'S_Updated_At', 'S_Catalog_Number',
      'S_Slug', 'S_Lyrics_URL', 'S_MIDI_URL', 'S_Full_Mix_URL', 'S_Likes_Count',
      'S_Is_Deleted', 'S_Deleted_At', 'S_Deleted_By',
      'U_ID', 'U_Email', 'U_Username', 'U_Full_Name', 'U_Avatar_URL', 'U_Bio',
      'U_Created_At', 'U_Updated_At', 'U_Role', 'U_Onboarding_Complete'
    ];

    const rows = exportData.map(row => [
      row.pa_id, row.pa_performance_id, row.pa_pitch_accuracy, row.pa_timing_accuracy,
      row.pa_note_transition_score, row.pa_overall_score, JSON.stringify(row.pa_detailed_feedback || {}),
      row.pa_created_at, row.pa_status_code, row.pa_error_message, row.pa_coaching_response,
      row.p_id, row.p_user_id, row.p_song_id, row.p_vocal_recording_url, row.p_duration_seconds,
      row.p_score, JSON.stringify(row.p_vocal_score_data || {}), row.p_created_at, row.p_updated_at,
      row.p_video_recording_url, row.p_video_thumbnail_url, row.p_video_duration_seconds, row.p_recording_id,
      row.r_id, row.r_user_id, row.r_song_id, row.r_recording_url, row.r_recording_type,
      row.r_duration, row.r_created_at, row.r_updated_at,
      row.r_title, row.r_shared_url, row.r_shared, row.r_shared_at, row.r_vocals_only_url,
      row.r_upload_status, row.r_local_uri, row.r_processing_command_id, row.r_processing_status, row.r_processing_error,
      row.r_vocals_preprocessed_url, row.r_preprocessing_status, row.r_preprocessing_error, row.r_audio_sync_offset,
      row.s_id, row.s_title, row.s_artist, row.s_genre, row.s_release_year, row.s_difficulty,
      row.s_duration, row.s_audio_url, row.s_cover_url, row.s_lead_vocal_url,
      row.s_distribution_eligible, row.s_created_at, row.s_updated_at, row.s_catalog_number,
      row.s_slug, row.s_lyrics_url, row.s_midi_url, row.s_full_mix_url, row.s_likes_count,
      row.s_is_deleted, row.s_deleted_at, row.s_deleted_by,
      row.u_id, row.u_email, row.u_username, row.u_full_name, row.u_avatar_url, row.u_bio,
      row.u_created_at, row.u_updated_at, row.u_role, row.u_onboarding_complete
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell !== null && cell !== undefined ? String(cell).replace(/"/g, '""') : ''}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance_analysis_debug_export.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const currentData = hasActiveFilters ? flatData : allData;

  if (loading && currentData.length === 0) {
    return (
      <div className="container mx-auto p-6 flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kod-accent"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Technical Debug Console</h1>
        <p className="text-white/70">Performance analysis data export and debugging</p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 flex items-center text-red-500">
          <AlertTriangle size={20} className="mr-2 flex-shrink-0" />
          <p>{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300 text-sm">Dismiss</button>
        </div>
      )}

      <div className="card mb-6">
        <h2 className="text-xl font-bold mb-4">Filters</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              <User size={16} className="inline mr-1" />
              User ID (Optional)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={filterUserId}
                onChange={(e) => { setFilterUserId(e.target.value); setCurrentPage(1); }}
                placeholder="Enter user UUID or leave empty for all users"
                className="flex-1 px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                <Disc size={16} className="inline mr-1" />
                Recording ID (Optional)
              </label>
              <input
                type="text"
                value={filterRecordingId}
                onChange={(e) => { setFilterRecordingId(e.target.value); setCurrentPage(1); }}
                placeholder="Enter recording UUID"
                className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                <BarChart2 size={16} className="inline mr-1" />
                Performance ID (Optional)
              </label>
              <input
                type="text"
                value={filterPerformanceId}
                onChange={(e) => { setFilterPerformanceId(e.target.value); setCurrentPage(1); }}
                placeholder="Enter performance UUID"
                className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
              />
            </div>
          </div>

          {hasActiveFilters && (
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onDateRangeChange={(start, end) => {
                setStartDate(start);
                setEndDate(end);
                setCurrentPage(1);
              }}
            />
          )}

          <div className="flex gap-3 pt-2">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="btn-primary flex items-center"
            >
              <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh Data
            </button>

            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="btn-outline flex items-center"
              >
                Clear Filters
              </button>
            )}

            <button
              onClick={handleExportCSV}
              disabled={currentData.length === 0}
              className="btn-outline flex items-center"
            >
              <Download size={16} className="mr-2" />
              Export CSV ({currentData.length} records)
            </button>
          </div>
        </div>
      </div>

      <div className="card mb-6">
        <h2 className="text-xl font-bold mb-4">Audio Sync Recalculation</h2>
        <p className="text-white/70 mb-4">
          Recalculate audio sync offsets for video recordings.
        </p>
        <div className="flex gap-3">
          <button
            onClick={handleBulkRecalculateSync}
            disabled={bulkCalculatingSync}
            className="btn-primary flex items-center"
          >
            {bulkCalculatingSync ? (
              <>
                <RefreshCw size={16} className="mr-2 animate-spin" />
                Processing {syncProgress.current}/{syncProgress.total}...
              </>
            ) : (
              <>
                <RefreshCw size={16} className="mr-2" />
                Bulk Recalculate Missing Sync Offsets
              </>
            )}
          </button>
        </div>
        {bulkCalculatingSync && syncProgress.total > 0 && (
          <div className="mt-4">
            <div className="w-full bg-kod-purple-700 rounded-full h-2 overflow-hidden">
              <div
                className="bg-kod-accent h-full transition-all duration-300"
                style={{ width: `${(syncProgress.current / syncProgress.total) * 100}%` }}
              />
            </div>
            <p className="text-sm text-white/60 mt-2">
              Processing recording {syncProgress.current} of {syncProgress.total}
            </p>
          </div>
        )}
      </div>

      {/* Main Data Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">
            Performance Analysis Data
            {!hasActiveFilters && (
              <span className="text-sm font-normal text-white/60 ml-2">
                ({allData.length} loaded across {dayGroups.length} days)
              </span>
            )}
            {hasActiveFilters && totalCount > 0 && (
              <span className="text-sm font-normal text-white/60 ml-2">
                ({totalCount} total records)
              </span>
            )}
          </h2>
        </div>

        {currentData.length === 0 && !loading ? (
          <div className="text-center py-12">
            <Search size={48} className="mx-auto text-white/30 mb-4" />
            <p className="text-white/60">No performance analysis data found</p>
          </div>
        ) : hasActiveFilters ? (
          <>
            <RecordTable
              rows={flatData}
              retryingRecordings={retryingRecordings}
              retryingVideoProcessing={retryingVideoProcessing}
              deletingRows={deletingRows}
              rescoringRows={rescoringRows}
              preprocessingRecordings={preprocessingRecordings}
              syncingRecordings={syncingRecordings}
              onRetryVocal={handleRetryVocalProcessing}
              onRetryVideo={handleRetryVideoProcessing}
              onDelete={handleDeletePerformance}
              onRescore={handleRescore}
              onPreprocess={handleTriggerPreprocessing}
              onRecalculateSync={handleSingleRecalculateSync}
              onViewReport={(row) => {
                setSelectedReport(row.pa_report_compressed_b64);
                setSelectedReportTitle(`${row.s_title} - ${row.s_artist}`);
                setSelectedLyricsUrl(row.s_lyrics_url || undefined);
                setSelectedVocalUrl(row.r_vocals_only_url || row.p_vocal_recording_url || undefined);
                setSelectedBackingUrl(row.s_lead_vocal_url || undefined);
                setSelectedFinalUrl(row.r_mixed_recording_url || row.r_final_video_url || undefined);
                setReportModalOpen(true);
              }}
            />
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-kod-purple-700">
                <div className="text-sm text-white/60">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="btn-outline px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="btn-outline px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-3">
            {dayGroups.map((group) => (
              <DaySection
                key={group.date}
                group={group}
                expanded={expandedDays.has(group.date)}
                onToggle={() => toggleDay(group.date)}
                retryingRecordings={retryingRecordings}
                retryingVideoProcessing={retryingVideoProcessing}
                deletingRows={deletingRows}
                rescoringRows={rescoringRows}
                preprocessingRecordings={preprocessingRecordings}
                syncingRecordings={syncingRecordings}
                onRetryVocal={handleRetryVocalProcessing}
                onRetryVideo={handleRetryVideoProcessing}
                onDelete={handleDeletePerformance}
                onRescore={handleRescore}
                onPreprocess={handleTriggerPreprocessing}
                onRecalculateSync={handleSingleRecalculateSync}
                onViewReport={(row) => {
                  setSelectedReport(row.pa_report_compressed_b64);
                  setSelectedReportTitle(`${row.s_title} - ${row.s_artist}`);
                  setSelectedLyricsUrl(row.s_lyrics_url || undefined);
                  setSelectedVocalUrl(row.r_vocals_only_url || row.p_vocal_recording_url || undefined);
                  setSelectedBackingUrl(row.s_lead_vocal_url || undefined);
                  setSelectedFinalUrl(row.r_mixed_recording_url || row.r_final_video_url || undefined);
                  setReportModalOpen(true);
                }}
              />
            ))}

            <div ref={sentinelRef} className="py-4 flex justify-center">
              {loadingMore && (
                <div className="flex items-center gap-2 text-white/60">
                  <Loader2 size={20} className="animate-spin" />
                  <span className="text-sm">Loading older records...</span>
                </div>
              )}
              {!hasMore && dayGroups.length > 0 && (
                <p className="text-sm text-white/40">No more records to load</p>
              )}
            </div>
          </div>
        )}
      </div>

      <ReportViewerModal
        isOpen={reportModalOpen}
        onClose={() => {
          setReportModalOpen(false);
          setSelectedReport(null);
          setSelectedLyricsUrl(undefined);
          setSelectedVocalUrl(undefined);
          setSelectedBackingUrl(undefined);
          setSelectedFinalUrl(undefined);
        }}
        compressedData={selectedReport}
        title={`Report: ${selectedReportTitle}`}
        lyricsUrl={selectedLyricsUrl}
        vocalRecordingUrl={selectedVocalUrl}
        leadVocalUrl={selectedBackingUrl}
        finalRecordingUrl={selectedFinalUrl}
      />
    </div>
  );
};

interface DaySectionProps {
  group: DayGroup;
  expanded: boolean;
  onToggle: () => void;
  retryingRecordings: Set<string>;
  retryingVideoProcessing: Set<string>;
  deletingRows: Set<string>;
  rescoringRows: Set<string>;
  preprocessingRecordings: Set<string>;
  syncingRecordings: Set<string>;
  onRetryVocal: (recordingId: string, recordingUrl: string) => void;
  onRetryVideo: (recordingId: string, recordingUrl: string, vocalsOnlyUrl: string, backingTrackUrl?: string) => void;
  onDelete: (paId: string, pId: string) => void;
  onRescore: (row: PerformanceAnalysisRow) => void;
  onPreprocess: (recordingId: string) => void;
  onRecalculateSync: (row: PerformanceAnalysisRow) => void;
  onViewReport: (row: PerformanceAnalysisRow) => void;
}

const DaySection: React.FC<DaySectionProps> = ({
  group,
  expanded,
  onToggle,
  ...tableProps
}) => {
  return (
    <div className="border border-kod-purple-700 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-kod-purple-800/50 hover:bg-kod-purple-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          <span className="font-semibold text-white">{formatDateHeader(group.date)}</span>
        </div>
        <span className="bg-kod-accent/20 text-kod-accent px-3 py-1 rounded-full text-sm font-medium">
          {group.rows.length} recording{group.rows.length !== 1 ? 's' : ''}
        </span>
      </button>

      {expanded && (
        <div className="p-2">
          <RecordTable rows={group.rows} {...tableProps} />
        </div>
      )}
    </div>
  );
};

interface RecordTableProps {
  rows: PerformanceAnalysisRow[];
  retryingRecordings: Set<string>;
  retryingVideoProcessing: Set<string>;
  deletingRows: Set<string>;
  rescoringRows: Set<string>;
  preprocessingRecordings: Set<string>;
  syncingRecordings: Set<string>;
  onRetryVocal: (recordingId: string, recordingUrl: string) => void;
  onRetryVideo: (recordingId: string, recordingUrl: string, vocalsOnlyUrl: string, backingTrackUrl?: string) => void;
  onDelete: (paId: string, pId: string) => void;
  onRescore: (row: PerformanceAnalysisRow) => void;
  onPreprocess: (recordingId: string) => void;
  onRecalculateSync: (row: PerformanceAnalysisRow) => void;
  onViewReport: (row: PerformanceAnalysisRow) => void;
}

const RecordTable: React.FC<RecordTableProps> = ({
  rows,
  retryingRecordings,
  retryingVideoProcessing,
  deletingRows,
  rescoringRows,
  preprocessingRecordings,
  syncingRecordings,
  onRetryVocal,
  onRetryVideo,
  onDelete,
  onRescore,
  onPreprocess,
  onRecalculateSync,
  onViewReport,
}) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-kod-purple-600">
            <th className="text-left py-3 px-2 font-medium text-white/70">Song</th>
            <th className="text-left py-3 px-2 font-medium text-white/70">User</th>
            <th className="text-right py-3 px-2 font-medium text-white/70">Overall</th>
            <th className="text-right py-3 px-2 font-medium text-white/70">Pitch</th>
            <th className="text-right py-3 px-2 font-medium text-white/70">Timing</th>
            <th className="text-right py-3 px-2 font-medium text-white/70">Transition</th>
            <th className="text-center py-3 px-2 font-medium text-white/70">Has Vocals</th>
            <th className="text-center py-3 px-2 font-medium text-white/70">Preprocessing</th>
            <th className="text-center py-3 px-2 font-medium text-white/70">Video Status</th>
            <th className="text-center py-3 px-2 font-medium text-white/70">Vocals URL</th>
            <th className="text-center py-3 px-2 font-medium text-white/70">Vocal Reference URL</th>
            <th className="text-center py-3 px-2 font-medium text-white/70">Final Video</th>
            <th className="text-center py-3 px-2 font-medium text-white/70">Backing Track</th>
            <th className="text-center py-3 px-2 font-medium text-white/70">Sync Offset</th>
            <th className="text-left py-3 px-2 font-medium text-white/70">Created</th>
            <th className="text-center py-3 px-2 font-medium text-white/70">Report</th>
            <th className="text-center py-3 px-2 font-medium text-white/70">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <RecordRow
              key={row.pa_id}
              row={row}
              retryingRecordings={retryingRecordings}
              retryingVideoProcessing={retryingVideoProcessing}
              deletingRows={deletingRows}
              rescoringRows={rescoringRows}
              preprocessingRecordings={preprocessingRecordings}
              syncingRecordings={syncingRecordings}
              onRetryVocal={onRetryVocal}
              onRetryVideo={onRetryVideo}
              onDelete={onDelete}
              onRescore={onRescore}
              onPreprocess={onPreprocess}
              onRecalculateSync={onRecalculateSync}
              onViewReport={onViewReport}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface RecordRowProps {
  row: PerformanceAnalysisRow;
  retryingRecordings: Set<string>;
  retryingVideoProcessing: Set<string>;
  deletingRows: Set<string>;
  rescoringRows: Set<string>;
  preprocessingRecordings: Set<string>;
  syncingRecordings: Set<string>;
  onRetryVocal: (recordingId: string, recordingUrl: string) => void;
  onRetryVideo: (recordingId: string, recordingUrl: string, vocalsOnlyUrl: string, backingTrackUrl?: string) => void;
  onDelete: (paId: string, pId: string) => void;
  onRescore: (row: PerformanceAnalysisRow) => void;
  onPreprocess: (recordingId: string) => void;
  onRecalculateSync: (row: PerformanceAnalysisRow) => void;
  onViewReport: (row: PerformanceAnalysisRow) => void;
}

const RecordRow: React.FC<RecordRowProps> = ({
  row,
  retryingRecordings,
  retryingVideoProcessing,
  deletingRows,
  rescoringRows,
  preprocessingRecordings,
  syncingRecordings,
  onRetryVocal,
  onRetryVideo,
  onDelete,
  onRescore,
  onPreprocess,
  onRecalculateSync,
  onViewReport,
}) => {
  return (
    <tr className="border-b border-kod-purple-700/50 hover:bg-kod-purple-700/30">
      <td className="py-3 px-2">
        <div className="flex items-center gap-2">
          <img src={row.s_cover_url} alt={row.s_title} className="w-10 h-10 rounded object-cover" />
          <div className="min-w-0">
            <div className="font-medium truncate">{row.s_title}</div>
            <div className="text-xs text-white/60 truncate">{row.s_artist}</div>
          </div>
        </div>
      </td>
      <td className="py-3 px-2">
        <div className="text-xs truncate max-w-[150px]" title={row.u_email || row.p_user_id || 'Unknown'}>
          {row.u_email || (row.p_user_id ? row.p_user_id.substring(0, 8) + '...' : 'Unknown')}
        </div>
      </td>
      <td className="py-3 px-2 text-right">
        <span className="font-semibold">{row.pa_overall_score?.toFixed(1) || 'N/A'}</span>
      </td>
      <td className="py-3 px-2 text-right text-white/80">{row.pa_pitch_accuracy?.toFixed(1) || 'N/A'}</td>
      <td className="py-3 px-2 text-right text-white/80">{row.pa_timing_accuracy?.toFixed(1) || 'N/A'}</td>
      <td className="py-3 px-2 text-right text-white/80">{row.pa_note_transition_score?.toFixed(1) || 'N/A'}</td>
      <td className="py-3 px-2 text-center">
        {row.r_vocals_only_url ? (
          <span className="text-green-400">Yes</span>
        ) : (
          <button
            onClick={() => onRetryVocal(row.r_id, row.r_recording_url)}
            disabled={retryingRecordings.has(row.r_id)}
            className="text-kod-accent hover:text-kod-accent/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 mx-auto"
          >
            <RotateCw size={14} className={retryingRecordings.has(row.r_id) ? 'animate-spin' : ''} />
            <span className="text-xs">No (Retry)</span>
          </button>
        )}
      </td>
      <td className="py-3 px-2 text-center">
        {!row.r_vocals_only_url ? (
          <span className="text-white/40 text-xs">-</span>
        ) : row.r_vocals_preprocessed_url ? (
          <div className="flex items-center gap-1 justify-center">
            <span className="text-green-400 text-xs">Done</span>
            <button
              onClick={() => onPreprocess(row.r_id)}
              disabled={preprocessingRecordings.has(row.r_id)}
              className="text-white/40 hover:text-kod-accent disabled:opacity-50 disabled:cursor-not-allowed"
              title="Re-run preprocessing"
            >
              <RotateCw size={12} className={preprocessingRecordings.has(row.r_id) ? 'animate-spin' : ''} />
            </button>
          </div>
        ) : row.r_preprocessing_status === 'processing' ? (
          <span className="text-yellow-400 text-xs">Processing...</span>
        ) : row.r_preprocessing_status === 'failed' ? (
          <button
            onClick={() => onPreprocess(row.r_id)}
            disabled={preprocessingRecordings.has(row.r_id)}
            className="text-red-400 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 mx-auto"
            title={row.r_preprocessing_error || 'Failed - click to retry'}
          >
            <span className="text-xs">Failed</span>
            <RotateCw size={14} className={preprocessingRecordings.has(row.r_id) ? 'animate-spin' : ''} />
          </button>
        ) : (
          <button
            onClick={() => onPreprocess(row.r_id)}
            disabled={preprocessingRecordings.has(row.r_id)}
            className="text-kod-accent hover:text-kod-accent/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 mx-auto"
            title="Trigger audio preprocessing"
          >
            <span className="text-xs">Start</span>
            <RotateCw size={14} className={preprocessingRecordings.has(row.r_id) ? 'animate-spin' : ''} />
          </button>
        )}
      </td>
      <td className="py-3 px-2 text-center">
        {row.r_recording_type !== 'video' ? (
          <span className="text-white/40 text-xs">N/A</span>
        ) : row.r_final_video_url ? (
          <div className="flex items-center gap-1 justify-center">
            <span className="text-green-400 text-xs">Done</span>
            {row.r_vocals_only_url && (
              <button
                onClick={() => onRetryVideo(row.r_id, row.r_recording_url, row.r_vocals_only_url, row.s_audio_url)}
                disabled={retryingVideoProcessing.has(row.r_id)}
                className="text-white/40 hover:text-kod-accent disabled:opacity-50 disabled:cursor-not-allowed"
                title="Re-process video with backing track"
              >
                <RotateCw size={12} className={retryingVideoProcessing.has(row.r_id) ? 'animate-spin' : ''} />
              </button>
            )}
          </div>
        ) : row.r_video_processing_status === 'processing' ? (
          <span className="text-yellow-400 text-xs">Processing...</span>
        ) : row.r_video_processing_status === 'failed' || row.r_video_processing_error ? (
          <button
            onClick={() => onRetryVideo(row.r_id, row.r_recording_url, row.r_vocals_only_url, row.s_audio_url)}
            disabled={retryingVideoProcessing.has(row.r_id) || !row.r_vocals_only_url}
            className="text-red-400 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 mx-auto"
            title={row.r_video_processing_error || 'Failed'}
          >
            <Video size={14} />
            <RotateCw size={14} className={retryingVideoProcessing.has(row.r_id) ? 'animate-spin' : ''} />
          </button>
        ) : row.r_vocals_only_url ? (
          <button
            onClick={() => onRetryVideo(row.r_id, row.r_recording_url, row.r_vocals_only_url, row.s_audio_url)}
            disabled={retryingVideoProcessing.has(row.r_id)}
            className="text-kod-accent hover:text-kod-accent/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 mx-auto"
            title="Trigger video processing"
          >
            <Video size={14} />
            <RotateCw size={14} className={retryingVideoProcessing.has(row.r_id) ? 'animate-spin' : ''} />
          </button>
        ) : (
          <span className="text-white/40 text-xs">No vocals</span>
        )}
      </td>
      <td className="py-3 px-2 text-center">
        {row.r_vocals_only_url ? (
          <a href={row.r_vocals_only_url} target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300 text-xs" title={row.r_vocals_only_url}>Yes</a>
        ) : (
          <span className="text-white/30 text-xs">-</span>
        )}
      </td>
      <td className="py-3 px-2 text-center">
        {row.s_lead_vocal_url ? (
          <a href={row.s_lead_vocal_url} target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300 text-xs" title={row.s_lead_vocal_url}>Yes</a>
        ) : (
          <span className="text-white/30 text-xs">-</span>
        )}
      </td>
      <td className="py-3 px-2 text-center">
        {row.r_final_video_url ? (
          <a href={row.r_final_video_url} target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300 text-xs" title={row.r_final_video_url}>Yes</a>
        ) : (
          <span className="text-white/30 text-xs">-</span>
        )}
      </td>
      <td className="py-3 px-2 text-center">
        {row.s_audio_url ? (
          <a href={row.s_audio_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-xs" title={row.s_audio_url}>Yes</a>
        ) : (
          <span className="text-white/30 text-xs">-</span>
        )}
      </td>
      <td className="py-3 px-2 text-center">
        {row.r_recording_type === 'video' && row.s_audio_url ? (
          <div className="flex flex-col items-center gap-1">
            <span className={`text-xs font-mono ${row.r_audio_sync_offset !== null && row.r_audio_sync_offset !== undefined ? 'text-teal-400' : 'text-white/30'}`}>
              {row.r_audio_sync_offset !== null && row.r_audio_sync_offset !== undefined
                ? `${row.r_audio_sync_offset >= 0 ? '+' : ''}${row.r_audio_sync_offset.toFixed(3)}s`
                : 'N/A'}
            </span>
            <button
              onClick={() => onRecalculateSync(row)}
              disabled={syncingRecordings.has(row.r_id)}
              className="text-white/40 hover:text-teal-400 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Recalculate audio sync offset"
            >
              <RefreshCw size={12} className={syncingRecordings.has(row.r_id) ? 'animate-spin' : ''} />
            </button>
          </div>
        ) : (
          <span className="text-white/20 text-xs">-</span>
        )}
      </td>
      <td className="py-3 px-2 text-xs text-white/60">
        {new Date(row.pa_created_at).toLocaleString()}
      </td>
      <td className="py-3 px-2 text-center">
        {row.pa_report_compressed_b64 ? (
          <button
            onClick={() => onViewReport(row)}
            className="text-kod-accent hover:text-kod-accent/80 inline-flex items-center gap-1"
            title="View report data"
          >
            <FileText size={16} />
          </button>
        ) : (
          <span className="text-white/30">-</span>
        )}
      </td>
      <td className="py-3 px-2 text-center">
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => onRescore(row)}
            disabled={rescoringRows.has(row.pa_id) || !row.r_vocals_only_url || !row.s_lead_vocal_url}
            className="text-yellow-400 hover:text-yellow-300 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
            title={!row.r_vocals_only_url ? 'Cannot rescore: missing vocals URL' : !row.s_lead_vocal_url ? 'Cannot rescore: missing lead vocal reference' : 'Rescore performance'}
          >
            <Star size={16} className={rescoringRows.has(row.pa_id) ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => onDelete(row.pa_id, row.p_id)}
            disabled={deletingRows.has(row.pa_id) || rescoringRows.has(row.pa_id)}
            className="text-red-400 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
            title="Delete performance and analysis (keeps recording)"
          >
            <Trash2 size={16} className={deletingRows.has(row.pa_id) ? 'animate-pulse' : ''} />
          </button>
        </div>
      </td>
    </tr>
  );
};
